import { useEffect, useState } from 'react';
import { CircleHelp, CornerDownRight, Info, Pencil, PlugZap, ScanLine, Sparkles, Split } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { documentCaptureService } from '../../../services/documentCaptureService';
import { DocReviewReason, type ReviewIdentification, type ReviewQueueItem } from '../../../types/documentCapture.types';
import {
  CorroborationFacts,
  NetSuiteFacts,
  netSuiteFactFromError,
  netSuiteFactFromPreview,
  type NetSuiteFact,
} from './CorroborationFacts';

/**
 * What the two readers made of this scan (86d3x48q7 T13, bundle §4.1).
 *
 * Renders ABOVE the number field in {@link ScansReviewPanel} and reuses that panel's machinery
 * wholesale — the field, the debounced NetSuite preview, and the "Confirm is disabled until a
 * single live NS match resolves" gate. That reuse is the point: a machine's suggestion must survive
 * exactly the same NetSuite check as a number a person typed. Nothing here can link anything.
 *
 * ## Anti-reflexive by construction
 *
 * The measured error source in this feature is **people**, not readers: staff hand-matching misfiled
 * 2 of 45 links (~4.4%), including a cross-customer double-file 36 seconds apart. A UI that makes
 * agreeing easy would simply industrialise that. So, deliberately:
 *
 * - the field is **never pre-filled** — adopting a number is one explicit action;
 * - there is **no accept-all and no bulk approve**, at any scale;
 * - `Use this number` only FILLS the field; the NS check still has to pass;
 * - Enter is not bound to confirm (see ScansReviewPanel — the field is not in a form);
 * - the confirm button **names the number**, to put the reviewer's eye on the value they commit.
 *
 * The Unconfirmed panel not pre-filling matters for a second reason: pre-filling anchors the
 * reviewer on the decoder's number, which pollutes the decoder-accuracy sample the calibration
 * window exists to collect.
 */
interface IdentificationPanelProps {
  item: ReviewQueueItem;
  /** Fills the number field. Never links, never submits. */
  onAdopt: (docNumber: string) => void;
  /** Current field contents, so a conflict card can show which candidate is selected. */
  current: string;
}

export function IdentificationPanel({ item, onAdopt, current }: IdentificationPanelProps) {
  const id = item.identification;
  if (!id) return null;

  switch (item.reason) {
    case DocReviewReason.AiSuggestion:
      return <SuggestionCard id={id} onAdopt={onAdopt} />;
    case DocReviewReason.IdentificationConflict:
      return <ConflictCards id={id} onAdopt={onAdopt} current={current} />;
    case DocReviewReason.Unconfirmed:
      return <NoCorroboration id={id} onAdopt={onAdopt} variant="unconfirmed" />;
    case DocReviewReason.VerifierUnavailable:
      return <NoCorroboration id={id} onAdopt={onAdopt} variant="unavailable" />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------- a) suggestion

function SuggestionCard({ id, onAdopt }: { id: ReviewIdentification; onAdopt: (n: string) => void }) {
  if (!id.aiRead) return null;
  return (
    <div className="rounded-lg border border-info/30 bg-info/5">
      <div className="flex items-center gap-1.5 border-b border-info/20 px-3 py-2.5">
        <Sparkles className="h-3.5 w-3.5 flex-shrink-0 text-info" aria-hidden />
        <span className="text-xs font-semibold text-info">The second reader read this off the page</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="ml-auto rounded-sm text-info/80 hover:text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="How the second reader's suggestion is used"
            >
              <Info className="h-3 w-3" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {/* 86d3z2nze — no directional language. The panes stack below lg, so the scan is ABOVE
                this form as often as it is beside it. */}
            It never links on its own. You are the one who decides — read the number off the scan
            before you confirm.
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="p-3">
        {/* 19px is from the design bundle, which is the source of truth for layout: the number is
            deliberately oversized so it can be compared against the paper at a glance. */}
        <div className="font-mono text-[19px] font-bold leading-tight text-foreground">{id.aiRead}</div>
        <CorroborationFacts exists={id.aiExists} claimedByFile={id.aiClaimedByFile} entityName={id.aiEntityName} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3 h-8 border-info/45"
          onClick={() => onAdopt(id.aiRead!)}
        >
          <CornerDownRight className="mr-1.5 h-3 w-3" aria-hidden />
          Use this number
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- b) conflict

function ConflictCards({
  id,
  onAdopt,
  current,
}: {
  id: ReviewIdentification;
  onAdopt: (n: string) => void;
  current: string;
}) {
  // 86d3z2nzb — both candidates are looked up in NetSuite the moment the item opens, in parallel.
  const nsFacts = useNetSuiteFacts(id.decoderRead, id.aiRead);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
        <div className="flex items-start gap-2">
          <Split className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Two readers, two different numbers</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Neither number is assumed right — you decide. Check the number printed on the scan and
              pick the one that matches.
            </p>
          </div>
        </div>
      </div>

      {/*
        ⚠️ The two cards must stay VISUALLY IDENTICAL. No ordering weight, no "recommended", no colour
        advantage, no confidence score. Nothing has established which of these two readers is more
        reliable, so any visual preference here would be an invention.

        86d3z2nze — and the COPY above must not argue the point either. The shipped text used to read
        "Shown in reading order, not ranked — the paper on the left decides. On the calibration
        corpus, when these disagreed it was the filing that was wrong, not the barcode reader." Three
        separate defects, recorded so they are not re-drifted into:
          · the result was never measured — the 51-blob corpus compared the barcode reader against
            STAFF FILING and contained no second reader at all;
          · "the paper on the left decides" reads as "the left card wins", and is factually wrong
            below lg (the panes stack, so the scan is ABOVE) and between sm and lg (the only thing on
            the left is one of the two cards);
          · it raised a ranking in order to deny one, which plants the idea that a ranking exists.

        The direction, owner-decided 2026-08-07: say what the reviewer should DO, in the same voice as
        the conflict tooltip in `config/verification.ts`, and say NOTHING about the measurement
        window, calibration, or which reader is being assessed — the reviewer has no stake in it and
        cannot act on it. If the cards are identical, "not ranked" does not need saying. No
        directional language, ever. `e2e/helpers.ts` BANNED_WORDS now enforces the vocabulary half.
      */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <CandidateCard
          icon={<ScanLine className="h-3 w-3" aria-hidden />}
          source="Barcode reader"
          number={id.decoderRead}
          exists={id.decoderExists}
          claimedByFile={id.decoderClaimedByFile}
          entityName={id.decoderEntityName}
          nsFact={id.decoderRead ? nsFacts[id.decoderRead] : undefined}
          selected={!!id.decoderRead && current.trim().toUpperCase() === id.decoderRead.toUpperCase()}
          onSelect={onAdopt}
        />
        <CandidateCard
          icon={<Sparkles className="h-3 w-3" aria-hidden />}
          source="Second reader (AI)"
          number={id.aiRead}
          exists={id.aiExists}
          claimedByFile={id.aiClaimedByFile}
          entityName={id.aiEntityName}
          nsFact={id.aiRead ? nsFacts[id.aiRead] : undefined}
          selected={!!id.aiRead && current.trim().toUpperCase() === id.aiRead.toUpperCase()}
          onSelect={onAdopt}
        />
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 border-dashed"
        onClick={() => onAdopt('')}
      >
        <Pencil className="mr-1.5 h-3 w-3" aria-hidden />
        Neither — type the number off the paper
      </Button>
    </div>
  );
}

/**
 * Live NetSuite facts for BOTH candidates, keyed by number (86d3z2nzb).
 *
 * Fired on open rather than on click: the reviewer's whole job here is to tell two numbers apart,
 * and the customer + date on each is the thing that does it. Waiting for them to click one first
 * would mean the comparison can only ever be made serially, one candidate at a time — which is the
 * gap this closes.
 *
 * Rules this hook exists to keep:
 *  · the skeleton is set BEFORE either request goes out, so the panel paints immediately;
 *  · the two lookups run in PARALLEL and each lands on its own — a slow candidate must not hold up
 *    the other one's answer;
 *  · a failed lookup resolves to a stated reason, never to a silent blank and never to an existence
 *    claim (see {@link netSuiteFactFromError}).
 *
 * Reuses `GET document-capture/netsuite/preview/{docNumber}`, the same endpoint the number field
 * below already debounces into — no new endpoint, no snapshot, no migration.
 */
/**
 * Matches the number field's own debounce below. Long enough that arrow-key travel through the
 * queue costs nothing until the reviewer settles; short enough to feel immediate once they do.
 */
const NS_FACT_DEBOUNCE_MS = 250;

/**
 * A separator that cannot occur in a document number, so round-tripping the dep key can never
 * invent or lose a candidate. (`|` was the first choice and is wrong in principle — a number
 * containing it would split into phantom lookups and strand both real cards on their skeletons.)
 */
const KEY_SEP = '\u0000';

function useNetSuiteFacts(decoderRead: string | null, aiRead: string | null): Record<string, NetSuiteFact> {
  const [facts, setFacts] = useState<Record<string, NetSuiteFact>>({});

  // A primitive dep, so switching review items refetches and a re-render never does.
  const key = [decoderRead, aiRead].filter((n): n is string => !!n).join(KEY_SEP);

  useEffect(() => {
    const numbers = key ? key.split(KEY_SEP) : [];
    if (numbers.length === 0) {
      setFacts({});
      return;
    }

    let live = true;
    const controller = new AbortController();
    // Painted before anything goes out, and before the debounce — the panel must never sit blank
    // waiting on a timer.
    setFacts(Object.fromEntries(numbers.map((n) => [n, { kind: 'loading' } as NetSuiteFact])));

    // Debounced, and aborted on supersede. Both are load-bearing, not hygiene: the queue supports
    // ArrowUp/ArrowDown navigation (ReviewOverlay), which changes selection on EVERY keypress, and
    // each selection costs TWO live SuiteQL queries against an account limited to 5 concurrent
    // across 8 integrations. Holding an arrow key through a 40-item queue would have issued ~80
    // uncancellable NetSuite calls in a few seconds — the 2026-07-29 lane-starvation shape. Merely
    // discarding superseded RESULTS does not help; the request still runs and still holds a slot.
    const timer = setTimeout(() => {
      for (const number of numbers) {
        documentCaptureService
          .previewNetSuite(number, controller.signal)
          .then(netSuiteFactFromPreview, netSuiteFactFromError)
          .then(
            (fact) => {
              if (live) setFacts((prev) => ({ ...prev, [number]: fact }));
            },
            // Terminal handler: netSuiteFactFromPreview can itself throw on a malformed payload, and
            // without this the rejection escapes as an unhandled rejection (which e2e/fixtures.ts
            // fails the run on) while the card stays on its skeleton for ever.
            () => {
              if (live) setFacts((prev) => ({ ...prev, [number]: { kind: 'unreachable' } }));
            }
          );
      }
    }, NS_FACT_DEBOUNCE_MS);

    return () => {
      live = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [key]);

  return facts;
}

function CandidateCard({
  icon,
  source,
  number,
  exists,
  claimedByFile,
  entityName,
  nsFact,
  selected,
  onSelect,
}: {
  icon: React.ReactNode;
  source: string;
  number: string | null;
  exists: boolean;
  claimedByFile: string | null;
  entityName: string | null;
  /** Undefined only for the frame before the effect runs — rendered as the loading state. */
  nsFact: NetSuiteFact | undefined;
  selected: boolean;
  onSelect: (n: string) => void;
}) {
  if (!number) return null;
  return (
    <button
      type="button"
      onClick={() => onSelect(number)}
      aria-pressed={selected}
      className={cn(
        'rounded-lg border p-3 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-accent/40'
      )}
    >
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {icon}
        {source}
      </span>
      <span className="mt-1 block font-mono text-base font-bold text-foreground">{number}</span>
      <CorroborationFacts exists={exists} claimedByFile={claimedByFile} entityName={entityName} />
      <NetSuiteFacts fact={nsFact ?? { kind: 'loading' }} />
    </button>
  );
}

// ---------------------------------------------------------------- c) nothing corroborates it

/**
 * Two different failures that must never look like the same failure.
 *
 * `unconfirmed` — the check RAN and the AI declined to read a number. `unavailable` — the check
 * never happened at all, because the call itself failed. They need different fixes (the first is a
 * scan-quality question, the second is an outage), which is why the copy and tone differ and why
 * neither says "unreadable".
 */
function NoCorroboration({
  id,
  onAdopt,
  variant,
}: {
  id: ReviewIdentification;
  onAdopt: (n: string) => void;
  variant: 'unconfirmed' | 'unavailable';
}) {
  const unavailable = variant === 'unavailable';
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        unavailable ? 'border-destructive/30 bg-destructive/5' : 'border-warning/30 bg-warning/10'
      )}
    >
      <div className="flex items-start gap-2">
        {unavailable ? (
          <PlugZap className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" aria-hidden />
        ) : (
          <CircleHelp className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {/* 86d3yeh4z — the unconfirmed heading is the SAME words as the chip and the registry
                filter ("Only the barcode reader"), so a reviewer meets one name for one state.
                Not "the barcode read it" — a barcode gets read, it does not read. */}
            {unavailable ? 'There was no second reading' : 'Only the barcode reader'}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {unavailable
              ? 'The check never ran — that’s a system problem, not a paper problem. The barcode’s number is shown below; check it against the paper before you confirm.'
              : 'The barcode reader read a number, but the AI couldn’t read one off the page, so there’s nothing to compare it with.'}
          </p>

          {id.decoderRead && (
            <div className="mt-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                What the barcode reader got
              </span>
              <div className="mt-0.5 font-mono text-base font-bold text-foreground">{id.decoderRead}</div>
              <CorroborationFacts
                exists={id.decoderExists}
                claimedByFile={id.decoderClaimedByFile}
                entityName={id.decoderEntityName}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 h-8"
                onClick={() => onAdopt(id.decoderRead!)}
              >
                <CornerDownRight className="mr-1.5 h-3 w-3" aria-hidden />
                Use this number
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
